import { type CSSProperties } from "react";
import { EMPTY_FILTER_VALUE, isActiveFilter } from "@/lib/boardFilters";
import type { ViewColumn, ViewStatusOption } from "@/lib/boardView";
import { statusColorOf } from "@/lib/tokens";
import { t } from "@/lib/strings";
import type { FilterCond } from "@/lib/types";

interface Props {
  columns: ViewColumn[];
  filters: FilterCond[];
  onChange: (filters: FilterCond[]) => void;
}

function upsert(filters: FilterCond[], columnId: string, cond: FilterCond | null): FilterCond[] {
  const rest = filters.filter((c) => c.column_id !== columnId);
  return cond && isActiveFilter(cond) ? [...rest, cond] : rest;
}

export function FilterPanel({ columns, filters, onChange }: Props) {
  const anyActive = filters.some(isActiveFilter);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        // Fill the (now roomy) popover; minWidth:0 lets flex children shrink so a
        // long option label never forces a horizontal scrollbar.
        width: "100%",
        minWidth: 0,
      }}
    >
      {columns.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--ink-4)" }}>{t.filterNoColumns}</div>
      ) : null}
      {columns.map((col) => (
        <div key={col.id} style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          <div style={labelStyle}>{col.name}</div>
          <Control
            col={col}
            cond={filters.find((c) => c.column_id === col.id)}
            onSet={(cond) => onChange(upsert(filters, col.id, cond))}
          />
        </div>
      ))}
      {anyActive ? (
        <button type="button" onClick={() => onChange([])} style={clearStyle}>
          {t.filterClear}
        </button>
      ) : null}
    </div>
  );
}

function Control({
  col,
  cond,
  onSet,
}: {
  col: ViewColumn;
  cond: FilterCond | undefined;
  onSet: (cond: FilterCond | null) => void;
}) {
  if (col.type === "status" || col.type === "tags") {
    const allowed = new Set(cond?.op === "in" && Array.isArray(cond.value) ? cond.value : []);
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {(col.options ?? []).map((o) => {
          const on = allowed.has(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => {
                const next = new Set(allowed);
                if (on) next.delete(o.id);
                else next.add(o.id);
                onSet({ column_id: col.id, op: "in", value: [...next] });
              }}
              style={optionChipStyle(on)}
            >
              <ColorDot option={o} />
              {o.label}
            </button>
          );
        })}
        {(() => {
          // F10: an "Ohne / Leer" chip — matches rows with no (valid) value.
          const on = allowed.has(EMPTY_FILTER_VALUE);
          return (
            <button
              type="button"
              onClick={() => {
                const next = new Set(allowed);
                if (on) next.delete(EMPTY_FILTER_VALUE);
                else next.add(EMPTY_FILTER_VALUE);
                onSet({ column_id: col.id, op: "in", value: [...next] });
              }}
              style={{ ...chipStyle(on), fontStyle: "italic" }}
              title={t.filterEmptyHint}
            >
              {t.filterEmpty}
            </button>
          );
        })()}
      </div>
    );
  }
  if (col.type === "text" || col.type === "long_text") {
    const val = cond?.op === "contains" && typeof cond.value === "string" ? cond.value : "";
    return (
      <input
        value={val}
        onChange={(e) => onSet({ column_id: col.id, op: "contains", value: e.target.value })}
        placeholder={t.filterContains}
        style={inputStyle}
      />
    );
  }
  if (col.type === "checkbox") {
    const state = cond?.op === "is" ? (cond.value === true ? "checked" : "unchecked") : "all";
    return (
      <div style={{ display: "flex", gap: 5 }}>
        <Seg label={t.filterAll} active={state === "all"} onClick={() => onSet(null)} />
        <Seg
          label={t.filterChecked}
          active={state === "checked"}
          onClick={() => onSet({ column_id: col.id, op: "is", value: true })}
        />
        <Seg
          label={t.filterUnchecked}
          active={state === "unchecked"}
          onClick={() => onSet({ column_id: col.id, op: "is", value: false })}
        />
      </div>
    );
  }
  if (col.type === "date") {
    const op = cond?.op === "after" ? "after" : "before";
    const val =
      (cond?.op === "before" || cond?.op === "after") && typeof cond.value === "string"
        ? cond.value
        : "";
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <select
          value={op}
          onChange={(e) =>
            onSet({ column_id: col.id, op: e.target.value as "before" | "after", value: val })
          }
          style={selectStyle}
        >
          <option value="before">{t.filterBefore}</option>
          <option value="after">{t.filterAfter}</option>
        </select>
        <input
          type="date"
          value={val}
          onChange={(e) => onSet({ column_id: col.id, op, value: e.target.value })}
          style={{ ...inputStyle, flex: 1 }}
        />
      </div>
    );
  }
  return null;
}

function Seg({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={chipStyle(active)}>
      {label}
    </button>
  );
}

const labelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const inputStyle: CSSProperties = {
  height: 30,
  fontSize: 13,
  padding: "0 8px",
  borderRadius: 6,
  border: "1px solid var(--hairline)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  outline: "none",
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  height: 30,
};

// Colored dot matching the board's status-option color (same tint mapping the
// StatusPill uses), so filter chips read like the board's cells.
function ColorDot({ option }: { option: ViewStatusOption }) {
  const tint = statusColorOf(option.color);
  return (
    <span
      aria-hidden
      style={{
        width: 8,
        height: 8,
        borderRadius: 999,
        background: tint.dot,
        flexShrink: 0,
      }}
    />
  );
}

// Chip with an inline color dot: a touch more horizontal padding than the plain
// chip to seat the dot, and the label may ellipsize so a long option can't
// overflow the panel.
function optionChipStyle(active: boolean): CSSProperties {
  return {
    ...chipStyle(active),
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  };
}

function chipStyle(active: boolean): CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 500,
    padding: "3px 9px",
    borderRadius: 999,
    cursor: "pointer",
    border: `1px solid ${active ? "var(--color-brand-blue)" : "var(--hairline)"}`,
    background: active ? "var(--color-brand-blue)" : "transparent",
    color: active ? "#fff" : "var(--ink-2)",
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  };
}

const clearStyle: CSSProperties = {
  alignSelf: "flex-start",
  fontSize: 12,
  color: "var(--color-brand-blue)",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 0,
};
